import { useState, useEffect, useCallback, useRef } from 'react';
import {
  User, Upload, Search, Send, Trash2, Plus, X, Mail, Phone,
  Check, AlertCircle, Clock, RefreshCw, ExternalLink, Image as ImageIcon,
  MoreVertical, Eye, Loader2, Edit3, FileText, FileSpreadsheet, Building2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';

const HeadshotDeliveriesPanel = ({
  gallery,
  galleryId,
  photos,
  axiosConfig,
  backendUrl
}) => {
  // State
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDelivery, setSelectedDelivery] = useState(null);

  // Dialog states
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showCsvDialog, setShowCsvDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showMatchDialog, setShowMatchDialog] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [showAddPhotoDialog, setShowAddPhotoDialog] = useState(false);

  // CSV Import state
  const [csvFile, setCsvFile] = useState(null);
  const [csvPreview, setCsvPreview] = useState(null);
  const [csvLoading, setCsvLoading] = useState(false);
  const [csvImporting, setCsvImporting] = useState(false);
  const csvInputRef = useRef(null);

  // Bulk upload state
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const fileInputRef = useRef(null);

  // Edit form state
  const [editForm, setEditForm] = useState({ name: '', email: '', mobile: '' });
  const [saving, setSaving] = useState(false);

  // Other state
  const [matching, setMatching] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);
  const [sendSms, setSendSms] = useState(true);
  const [photoSearchTerm, setPhotoSearchTerm] = useState('');

  // Fetch deliveries
  const fetchDeliveries = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${backendUrl}/api/galleries/${galleryId}/headshot-deliveries`,
        axiosConfig
      );
      setDeliveries(response.data);
    } catch (error) {
      console.error('Error fetching deliveries:', error);
      toast.error('Failed to load deliveries');
    } finally {
      setLoading(false);
    }
  }, [galleryId, backendUrl, axiosConfig]);

  useEffect(() => {
    fetchDeliveries();
  }, [fetchDeliveries]);

  // Handle file selection for bulk upload
  const handleFilesSelected = (e) => {
    const files = Array.from(e.target.files || []);
    const imageFiles = files.filter(f => f.type.startsWith('image/'));
    if (imageFiles.length !== files.length) {
      toast.error('Some files were skipped (only images allowed)');
    }
    setUploadFiles(imageFiles);
  };

  // Handle drag and drop
  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter(f => f.type.startsWith('image/'));
    if (imageFiles.length !== files.length) {
      toast.error('Some files were skipped (only images allowed)');
    }
    setUploadFiles(prev => [...prev, ...imageFiles]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // Remove file from upload list
  const removeFile = (index) => {
    setUploadFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Bulk upload headshots
  const handleBulkUpload = async () => {
    if (uploadFiles.length === 0) {
      toast.error('Please select files to upload');
      return;
    }

    setUploading(true);
    setUploadProgress({ current: 0, total: uploadFiles.length });

    try {
      const formData = new FormData();
      uploadFiles.forEach(file => {
        formData.append('headshots', file);
      });

      const response = await axios.post(
        `${backendUrl}/api/galleries/${galleryId}/headshot-deliveries/bulk-upload`,
        formData,
        {
          ...axiosConfig,
          headers: {
            ...axiosConfig.headers,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded / progressEvent.total) * uploadFiles.length);
            setUploadProgress({ current: progress, total: uploadFiles.length });
          }
        }
      );

      toast.success(`Uploaded ${response.data.successful} of ${response.data.total} headshots`);
      if (response.data.failed > 0) {
        toast.error(`${response.data.failed} uploads failed`);
      }

      setShowUploadDialog(false);
      setUploadFiles([]);
      fetchDeliveries();
    } catch (error) {
      console.error('Error uploading headshots:', error);
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress({ current: 0, total: 0 });
    }
  };

  // Open edit dialog
  const handleEditDelivery = (delivery) => {
    setSelectedDelivery(delivery);
    setEditForm({
      name: delivery.name || '',
      email: delivery.email || '',
      mobile: delivery.mobile || ''
    });
    setShowEditDialog(true);
  };

  // Save contact details
  const handleSaveContact = async () => {
    if (!editForm.name?.trim()) {
      toast.error('Name is required');
      return;
    }
    if (!editForm.email?.trim()) {
      toast.error('Email is required');
      return;
    }

    setSaving(true);
    try {
      await axios.put(
        `${backendUrl}/api/galleries/${galleryId}/headshot-deliveries/${selectedDelivery.id}`,
        {
          name: editForm.name.trim(),
          email: editForm.email.trim(),
          mobile: editForm.mobile?.trim() || null
        },
        axiosConfig
      );

      toast.success('Contact details saved');
      setShowEditDialog(false);
      fetchDeliveries();
    } catch (error) {
      console.error('Error saving contact:', error);
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Run face matching
  const handleRunMatching = async (delivery) => {
    setMatching(true);
    try {
      const response = await axios.post(
        `${backendUrl}/api/galleries/${galleryId}/headshot-deliveries/${delivery.id}/match`,
        {},
        axiosConfig
      );

      toast.success(`Found ${response.data.match_count} matching photos`);
      fetchDeliveries();

      if (selectedDelivery?.id === delivery.id) {
        const updated = await axios.get(
          `${backendUrl}/api/galleries/${galleryId}/headshot-deliveries/${delivery.id}`,
          axiosConfig
        );
        setSelectedDelivery(updated.data);
      }
    } catch (error) {
      console.error('Error running matching:', error);
      toast.error(error.response?.data?.detail || 'Matching failed');
    } finally {
      setMatching(false);
    }
  };

  // Open match management dialog
  const handleManageMatches = async (delivery) => {
    try {
      const response = await axios.get(
        `${backendUrl}/api/galleries/${galleryId}/headshot-deliveries/${delivery.id}`,
        axiosConfig
      );
      setSelectedDelivery(response.data);
      setShowMatchDialog(true);
    } catch (error) {
      console.error('Error fetching delivery details:', error);
      toast.error('Failed to load delivery details');
    }
  };

  // Remove a match
  const handleRemoveMatch = async (matchId) => {
    try {
      await axios.delete(
        `${backendUrl}/api/galleries/${galleryId}/headshot-deliveries/${selectedDelivery.id}/matches/${matchId}`,
        axiosConfig
      );

      const response = await axios.get(
        `${backendUrl}/api/galleries/${galleryId}/headshot-deliveries/${selectedDelivery.id}`,
        axiosConfig
      );
      setSelectedDelivery(response.data);
      fetchDeliveries();
      toast.success('Match removed');
    } catch (error) {
      console.error('Error removing match:', error);
      toast.error('Failed to remove match');
    }
  };

  // Add a photo match manually
  const handleAddMatch = async (photoId) => {
    try {
      await axios.post(
        `${backendUrl}/api/galleries/${galleryId}/headshot-deliveries/${selectedDelivery.id}/matches/add`,
        { photo_id: photoId },
        axiosConfig
      );

      const response = await axios.get(
        `${backendUrl}/api/galleries/${galleryId}/headshot-deliveries/${selectedDelivery.id}`,
        axiosConfig
      );
      setSelectedDelivery(response.data);
      fetchDeliveries();
      setShowAddPhotoDialog(false);
      toast.success('Photo added to matches');
    } catch (error) {
      console.error('Error adding match:', error);
      toast.error(error.response?.data?.detail || 'Failed to add match');
    }
  };

  // Send delivery
  const handleSendDelivery = async () => {
    if (!selectedDelivery) return;

    if (!selectedDelivery.email) {
      toast.error('Please add contact details first');
      return;
    }

    setSending(true);
    try {
      const response = await axios.post(
        `${backendUrl}/api/galleries/${galleryId}/headshot-deliveries/${selectedDelivery.id}/send`,
        { send_email: sendEmail, send_sms: sendSms },
        axiosConfig
      );

      if (response.data.success) {
        toast.success('Delivery sent successfully!');
        setShowSendDialog(false);
        fetchDeliveries();
      } else {
        toast.error('Some deliveries failed. Check the results.');
      }
    } catch (error) {
      console.error('Error sending delivery:', error);
      toast.error(error.response?.data?.detail || 'Failed to send delivery');
    } finally {
      setSending(false);
    }
  };

  // Delete delivery
  const handleDeleteDelivery = async (delivery) => {
    if (!confirm(`Delete delivery for ${delivery.name || 'this person'}? This cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(
        `${backendUrl}/api/galleries/${galleryId}/headshot-deliveries/${delivery.id}`,
        axiosConfig
      );
      toast.success('Delivery deleted');
      fetchDeliveries();
    } catch (error) {
      console.error('Error deleting delivery:', error);
      toast.error('Failed to delete delivery');
    }
  };

  // CSV Import - Handle file selection
  const handleCsvFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }

    setCsvFile(file);
    setCsvPreview(null);
    setCsvLoading(true);

    try {
      const formData = new FormData();
      formData.append('csv_file', file);

      const response = await axios.post(
        `${backendUrl}/api/galleries/${galleryId}/headshot-deliveries/preview-csv`,
        formData,
        {
          ...axiosConfig,
          headers: {
            ...axiosConfig.headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setCsvPreview(response.data);
    } catch (error) {
      console.error('Error previewing CSV:', error);
      toast.error(error.response?.data?.detail || 'Failed to parse CSV file');
      setCsvFile(null);
    } finally {
      setCsvLoading(false);
    }
  };

  // CSV Import - Handle drag and drop
  const handleCsvDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      // Create a synthetic event to reuse the handler
      handleCsvFileSelected({ target: { files: [file] } });
    }
  };

  // CSV Import - Execute import
  const handleCsvImport = async () => {
    if (!csvFile || !csvPreview) return;

    setCsvImporting(true);
    try {
      const formData = new FormData();
      formData.append('csv_file', csvFile);

      const response = await axios.post(
        `${backendUrl}/api/galleries/${galleryId}/headshot-deliveries/import-csv`,
        formData,
        {
          ...axiosConfig,
          headers: {
            ...axiosConfig.headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      const { successful, failed, total_matches, deliveries_without_matches } = response.data;

      if (successful > 0) {
        toast.success(`Imported ${successful} deliveries with ${total_matches} photo matches`);
      }
      if (failed > 0) {
        toast.error(`${failed} rows failed to import`);
      }
      if (deliveries_without_matches > 0) {
        toast.warning(`${deliveries_without_matches} deliveries have no matching photos`);
      }

      setShowCsvDialog(false);
      setCsvFile(null);
      setCsvPreview(null);
      fetchDeliveries();
    } catch (error) {
      console.error('Error importing CSV:', error);
      toast.error(error.response?.data?.detail || 'Failed to import CSV');
    } finally {
      setCsvImporting(false);
    }
  };

  // CSV Import - Reset state
  const handleCloseCsvDialog = () => {
    setShowCsvDialog(false);
    setCsvFile(null);
    setCsvPreview(null);
    if (csvInputRef.current) {
      csvInputRef.current.value = '';
    }
  };

  // Get status badge
  const getStatusBadge = (delivery) => {
    const status = delivery.status;
    const configs = {
      draft: { label: 'Needs Info', className: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300' },
      pending: { label: 'Ready', className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' },
      matched: { label: 'Matched', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
      sent: { label: 'Sent', className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' },
      failed: { label: 'Failed', className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' }
    };
    const config = configs[status] || configs.draft;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  // Separate drafts from complete deliveries
  const drafts = deliveries.filter(d => d.status === 'draft' || !d.email);
  const completeDeliveries = deliveries.filter(d => d.status !== 'draft' && d.email);

  // Filter photos not already matched
  const availablePhotos = photos.filter(photo => {
    if (!selectedDelivery?.matches) return true;
    return !selectedDelivery.matches.some(m => m.photo_id === photo.id && !m.removed);
  });

  const filteredPhotos = availablePhotos.filter(photo =>
    photo.filename?.toLowerCase().includes(photoSearchTerm.toLowerCase())
  );

  return (
    <div className="h-full overflow-auto p-3 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 md:mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-slate-800 dark:text-white" style={{ fontFamily: 'Archivo, sans-serif' }}>
            Headshot Deliveries
          </h2>
          <p className="text-xs md:text-sm text-slate-500 dark:text-slate-400 mt-1">
            Upload headshots, then add contact details to deliver photos
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchDeliveries}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowCsvDialog(true)}
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Import from CSV
          </Button>
          <Button
            onClick={() => setShowUploadDialog(true)}
            className="bg-[#BF212F] hover:from-[#8B1923] hover:to-[#BF212F]"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload Headshots
          </Button>
        </div>
      </div>

      {/* Loading */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      ) : deliveries.length === 0 ? (
        /* Empty State */
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <User className="w-12 h-12 text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-600 dark:text-slate-300 mb-2">
              No Deliveries Yet
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 text-center mb-4 max-w-md">
              Upload reference headshots in bulk, or import a CSV file to automatically match photos by filename prefix.
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowCsvDialog(true)}
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Import from CSV
              </Button>
              <Button
                onClick={() => setShowUploadDialog(true)}
                className="bg-[#BF212F] hover:from-[#8B1923] hover:to-[#BF212F] text-white"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload Headshots
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Drafts Section - Headshots needing contact info */}
          {drafts.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-orange-600 dark:text-orange-400 mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Needs Contact Info ({drafts.length})
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {drafts.map((delivery) => (
                  <div
                    key={delivery.id}
                    className="group relative bg-white dark:bg-slate-800 rounded-xl border-2 border-orange-200 dark:border-orange-800 overflow-hidden"
                  >
                    {/* Headshot */}
                    <div
                      className="aspect-square bg-slate-100 dark:bg-slate-700 cursor-pointer"
                      onClick={() => handleEditDelivery(delivery)}
                    >
                      {delivery.headshot_thumbnail_url ? (
                        <img
                          src={delivery.headshot_thumbnail_url}
                          alt={delivery.name || 'Headshot'}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <User className="w-12 h-12 text-slate-300" />
                        </div>
                      )}
                    </div>
                    {/* Info */}
                    <div className="p-2">
                      <p className="text-xs font-medium text-slate-700 dark:text-slate-200 truncate">
                        {delivery.name || delivery.original_filename || 'Unknown'}
                      </p>
                      {/* Match count or Find Matches button */}
                      {delivery.match_count > 0 ? (
                        <button
                          onClick={() => handleManageMatches(delivery)}
                          className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 mt-1"
                        >
                          <ImageIcon className="w-3 h-3" />
                          {delivery.match_count} match{delivery.match_count !== 1 ? 'es' : ''} found
                          {delivery.match_method === 'prefix' && ' (prefix)'}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleRunMatching(delivery)}
                          disabled={matching}
                          className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 mt-1 disabled:opacity-50"
                        >
                          {matching ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Search className="w-3 h-3" />
                          )}
                          {delivery.match_method === 'prefix' ? 'Sync prefix matches' : 'Find matches'}
                        </button>
                      )}
                      <button
                        onClick={() => handleEditDelivery(delivery)}
                        className="text-xs text-orange-500 hover:text-orange-700 flex items-center gap-1 mt-1"
                      >
                        <Edit3 className="w-3 h-3" />
                        Add contact details
                      </button>
                    </div>
                    {/* Delete button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteDelivery(delivery);
                      }}
                      className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Complete Deliveries */}
          {completeDeliveries.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-600 dark:text-slate-400 mb-3 flex items-center gap-2">
                <User className="w-4 h-4" />
                Deliveries ({completeDeliveries.length})
              </h3>
              <div className="space-y-3">
                {completeDeliveries.map((delivery) => (
                  <Card key={delivery.id} className="overflow-hidden hover:shadow-md transition-shadow">
                    <div className="flex items-center p-4 gap-4">
                      {/* Headshot Thumbnail */}
                      <div className="w-14 h-14 rounded-full overflow-hidden bg-slate-100 dark:bg-slate-700 flex-shrink-0">
                        {delivery.headshot_thumbnail_url ? (
                          <img
                            src={delivery.headshot_thumbnail_url}
                            alt={delivery.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <User className="w-6 h-6 text-slate-400" />
                          </div>
                        )}
                      </div>

                      {/* Delivery Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-slate-800 dark:text-white truncate">
                            {delivery.name}
                          </h3>
                          {getStatusBadge(delivery)}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-slate-500 dark:text-slate-400 flex-wrap">
                          <span className="flex items-center gap-1 truncate">
                            <Mail className="w-3.5 h-3.5 flex-shrink-0" />
                            {delivery.email}
                          </span>
                          {delivery.mobile && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3.5 h-3.5" />
                              {delivery.mobile}
                            </span>
                          )}
                          {delivery.company && (
                            <span className="flex items-center gap-1">
                              <Building2 className="w-3.5 h-3.5" />
                              {delivery.company}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-xs text-slate-400 flex-wrap">
                          <span className="flex items-center gap-1">
                            <ImageIcon className="w-3 h-3" />
                            {delivery.match_count} matches
                          </span>
                          {delivery.reference_number && (
                            <span className="flex items-center gap-1 font-mono">
                              Ref: {delivery.reference_number}
                            </span>
                          )}
                          {delivery.email_sent && (
                            <span className="flex items-center gap-1 text-green-500">
                              <Check className="w-3 h-3" />
                              Email sent
                            </span>
                          )}
                          {delivery.sms_sent && (
                            <span className="flex items-center gap-1 text-green-500">
                              <Check className="w-3 h-3" />
                              SMS sent
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        {delivery.match_count === 0 ? (
                          <Button
                            size="sm"
                            onClick={() => handleRunMatching(delivery)}
                            disabled={matching}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                          >
                            {matching ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Search className="w-4 h-4 mr-2" />
                            )}
                            {delivery.match_method === 'prefix' ? 'Sync Matches' : 'Find Matches'}
                          </Button>
                        ) : (
                          <>
                            {/* Sync/Re-run button */}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRunMatching(delivery)}
                              disabled={matching}
                              title={delivery.match_method === 'prefix' ? 'Sync prefix matches' : 'Re-run face matching'}
                            >
                              <RefreshCw className={`w-4 h-4 ${matching ? 'animate-spin' : ''}`} />
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleManageMatches(delivery)}
                              className="bg-slate-100 hover:bg-slate-200 text-slate-700"
                            >
                              <Eye className="w-4 h-4 mr-2" />
                              View {delivery.match_count} Match{delivery.match_count !== 1 ? 'es' : ''}
                            </Button>
                          </>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedDelivery(delivery);
                            setShowSendDialog(true);
                          }}
                          disabled={delivery.match_count === 0 || !delivery.email}
                          title="Send delivery"
                        >
                          <Send className="w-4 h-4" />
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEditDelivery(delivery)}>
                              <Edit3 className="w-4 h-4 mr-2" />
                              Edit Details
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => window.open(`/headshot/${delivery.access_token}`, '_blank')}
                            >
                              <ExternalLink className="w-4 h-4 mr-2" />
                              Preview Link
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDeleteDelivery(delivery)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Bulk Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Upload Headshots</DialogTitle>
            <DialogDescription>
              Upload multiple reference headshots. You can add contact details after uploading.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            {/* Drop Zone */}
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-slate-200 dark:border-slate-600 hover:border-[#BF212F] rounded-xl p-8 text-center cursor-pointer transition-colors"
            >
              <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
              <p className="text-sm text-slate-600 dark:text-slate-300 mb-1">
                Drag & drop headshot images here
              </p>
              <p className="text-xs text-slate-400">
                or click to browse (JPG, PNG)
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFilesSelected}
                className="hidden"
              />
            </div>

            {/* Selected Files */}
            {uploadFiles.length > 0 && (
              <div className="space-y-2">
                <Label>Selected Files ({uploadFiles.length})</Label>
                <div className="max-h-[200px] overflow-auto border rounded-lg p-2 space-y-1">
                  {uploadFiles.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-700 rounded"
                    >
                      <span className="text-sm truncate flex-1">{file.name}</span>
                      <button
                        onClick={() => removeFile(index)}
                        className="p-1 hover:bg-slate-200 dark:hover:bg-slate-600 rounded"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Upload Progress */}
            {uploading && (
              <div className="space-y-2 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-blue-800 dark:text-blue-200 font-medium">
                    Uploading headshots...
                  </span>
                  <span className="text-blue-600 dark:text-blue-300">
                    {uploadProgress.current} / {uploadProgress.total}
                  </span>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowUploadDialog(false);
                setUploadFiles([]);
              }}
              disabled={uploading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleBulkUpload}
              disabled={uploading || uploadFiles.length === 0}
              className="bg-[#BF212F] hover:bg-[#8B1923]"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload {uploadFiles.length} Headshot{uploadFiles.length !== 1 ? 's' : ''}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Contact Details Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              {selectedDelivery?.headshot_thumbnail_url && (
                <img
                  src={selectedDelivery.headshot_thumbnail_url}
                  alt=""
                  className="w-12 h-12 rounded-full object-cover"
                />
              )}
              <span>Edit Contact Details</span>
            </DialogTitle>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name *</Label>
              <Input
                id="edit-name"
                placeholder="John Smith"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-email">Email Address *</Label>
              <Input
                id="edit-email"
                type="email"
                placeholder="john@example.com"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-mobile">Mobile Number (Optional)</Label>
              <Input
                id="edit-mobile"
                type="tel"
                placeholder="+61 412 345 678"
                value={editForm.mobile}
                onChange={(e) => setEditForm({ ...editForm, mobile: e.target.value })}
              />
              <p className="text-xs text-slate-500">
                For SMS delivery. Australian or international format.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowEditDialog(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveContact}
              disabled={saving}
              className="bg-[#BF212F] hover:bg-[#8B1923]"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Save Details
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Match Management Dialog */}
      <Dialog open={showMatchDialog} onOpenChange={setShowMatchDialog}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              {selectedDelivery?.headshot_thumbnail_url && (
                <img
                  src={selectedDelivery.headshot_thumbnail_url}
                  alt=""
                  className="w-10 h-10 rounded-full object-cover"
                />
              )}
              <span>{selectedDelivery?.name || 'Unknown'}'s Matches</span>
              <Badge variant="secondary">{selectedDelivery?.match_count || 0} photos</Badge>
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-auto py-4">
            <div className="flex gap-2 mb-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => selectedDelivery && handleRunMatching(selectedDelivery)}
                disabled={matching}
              >
                {matching ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                {selectedDelivery?.match_method === 'prefix' ? 'Sync Matches' : 'Re-run Matching'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAddPhotoDialog(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Photo
              </Button>
            </div>

            {selectedDelivery?.matches?.length > 0 ? (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                {selectedDelivery.matches.map((match) => (
                  <div
                    key={match.id}
                    className="relative group rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-700 aspect-square"
                  >
                    <img
                      src={match.photo_thumbnail_url}
                      alt={match.photo_filename}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-1 left-1">
                      <Badge
                        className={`text-xs ${
                          match.auto_matched
                            ? 'bg-blue-500 text-white'
                            : 'bg-purple-500 text-white'
                        }`}
                      >
                        {match.auto_matched ? `${match.similarity?.toFixed(0)}%` : 'Manual'}
                      </Badge>
                    </div>
                    <button
                      onClick={() => handleRemoveMatch(match.id)}
                      className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Search className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>
                  {selectedDelivery?.match_method === 'prefix'
                    ? 'No photos found matching this prefix. Click "Sync Matches" or add photos manually.'
                    : 'No matches found. Click "Re-run Matching" to find photos.'}
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMatchDialog(false)}>
              Close
            </Button>
            <Button
              onClick={() => {
                setShowMatchDialog(false);
                setShowSendDialog(true);
              }}
              disabled={!selectedDelivery?.match_count || !selectedDelivery?.email}
              className="bg-[#BF212F] hover:bg-[#8B1923]"
            >
              <Send className="w-4 h-4 mr-2" />
              Send to Recipient
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Photo Dialog */}
      <Dialog open={showAddPhotoDialog} onOpenChange={setShowAddPhotoDialog}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Add Photo to Matches</DialogTitle>
            <DialogDescription>
              Select a photo to manually add to {selectedDelivery?.name || 'this delivery'}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <Input
              placeholder="Search photos by filename..."
              value={photoSearchTerm}
              onChange={(e) => setPhotoSearchTerm(e.target.value)}
              className="mb-4"
            />
          </div>

          <div className="flex-1 overflow-auto">
            <div className="grid grid-cols-4 gap-2">
              {filteredPhotos.slice(0, 50).map((photo) => (
                <button
                  key={photo.id}
                  onClick={() => handleAddMatch(photo.id)}
                  className="relative aspect-square rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-700 hover:ring-2 hover:ring-[#BF212F] transition-all"
                >
                  <img
                    src={photo.thumbnail_url || photo.thumbnail_path}
                    alt={photo.filename}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
            {filteredPhotos.length > 50 && (
              <p className="text-center text-sm text-slate-500 mt-4">
                Showing first 50 of {filteredPhotos.length} photos. Use search to find more.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddPhotoDialog(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Delivery Dialog */}
      <Dialog open={showSendDialog} onOpenChange={setShowSendDialog}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>Send Delivery</DialogTitle>
            <DialogDescription>
              Send {selectedDelivery?.match_count} photo(s) to {selectedDelivery?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg space-y-2">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-slate-500" />
                <span>{selectedDelivery?.email || 'No email'}</span>
              </div>
              {selectedDelivery?.mobile && (
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-slate-500" />
                  <span>{selectedDelivery?.mobile}</span>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <Label>Send via:</Label>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="send-email"
                  checked={sendEmail}
                  onCheckedChange={setSendEmail}
                  disabled={!selectedDelivery?.email}
                />
                <Label htmlFor="send-email" className="font-normal cursor-pointer">
                  Email
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="send-sms"
                  checked={sendSms}
                  onCheckedChange={setSendSms}
                  disabled={!selectedDelivery?.mobile}
                />
                <Label
                  htmlFor="send-sms"
                  className={`font-normal cursor-pointer ${!selectedDelivery?.mobile ? 'text-slate-400' : ''}`}
                >
                  SMS {!selectedDelivery?.mobile && '(no mobile number)'}
                </Label>
              </div>
            </div>

            {(selectedDelivery?.email_sent || selectedDelivery?.sms_sent) && (
              <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-yellow-600 mt-0.5" />
                  <div className="text-sm text-yellow-700 dark:text-yellow-300">
                    <strong>Already sent:</strong>
                    {selectedDelivery?.email_sent && ' Email'}
                    {selectedDelivery?.email_sent && selectedDelivery?.sms_sent && ','}
                    {selectedDelivery?.sms_sent && ' SMS'}
                    <p className="text-xs mt-1">Sending again will resend the notification.</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowSendDialog(false)}
              disabled={sending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSendDelivery}
              disabled={sending || (!sendEmail && !sendSms) || !selectedDelivery?.email}
              className="bg-[#BF212F] hover:bg-[#8B1923]"
            >
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Now
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV Import Dialog */}
      <Dialog open={showCsvDialog} onOpenChange={handleCloseCsvDialog}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              Import from CSV
            </DialogTitle>
            <DialogDescription>
              Upload a CSV file to create deliveries with automatic photo matching by filename prefix.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-auto py-4 space-y-4">
            {/* Drop Zone */}
            {!csvFile && (
              <div
                onDrop={handleCsvDrop}
                onDragOver={(e) => e.preventDefault()}
                onClick={() => csvInputRef.current?.click()}
                className="border-2 border-dashed border-slate-200 dark:border-slate-600 hover:border-[#BF212F] rounded-xl p-8 text-center cursor-pointer transition-colors"
              >
                <FileSpreadsheet className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                <p className="text-sm text-slate-600 dark:text-slate-300 mb-1">
                  Drag & drop a CSV file here
                </p>
                <p className="text-xs text-slate-400 mb-4">
                  or click to browse
                </p>
                <div className="text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800 rounded-lg p-3 max-w-md mx-auto">
                  <p className="font-semibold mb-2">Required columns:</p>
                  <p className="font-mono">reference_number, first_name, last_name, email</p>
                  <p className="mt-2 text-slate-400">Optional: mobile, company</p>
                </div>
                <input
                  ref={csvInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleCsvFileSelected}
                  className="hidden"
                />
              </div>
            )}

            {/* Loading State */}
            {csvLoading && (
              <div className="flex flex-col items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-[#BF212F] mb-3" />
                <p className="text-sm text-slate-600">Analyzing CSV file...</p>
              </div>
            )}

            {/* CSV Preview */}
            {csvPreview && !csvLoading && (
              <div className="space-y-4">
                {/* File Info */}
                <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <div className="flex items-center gap-3">
                    <FileSpreadsheet className="w-5 h-5 text-green-500" />
                    <div>
                      <p className="text-sm font-medium">{csvFile?.name}</p>
                      <p className="text-xs text-slate-500">
                        {csvPreview.total_rows} rows found
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setCsvFile(null);
                      setCsvPreview(null);
                      if (csvInputRef.current) csvInputRef.current.value = '';
                    }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>

                {/* Summary */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-600">{csvPreview.valid_rows}</p>
                    <p className="text-xs text-green-600">Valid rows</p>
                  </div>
                  <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-center">
                    <p className="text-2xl font-bold text-red-600">{csvPreview.invalid_rows}</p>
                    <p className="text-xs text-red-600">Invalid rows</p>
                  </div>
                  <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg text-center">
                    <p className="text-2xl font-bold text-yellow-600">{csvPreview.rows_without_matches}</p>
                    <p className="text-xs text-yellow-600">No matches</p>
                  </div>
                </div>

                {/* Preview Table */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 dark:bg-slate-800">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium text-slate-600 dark:text-slate-300">Reference</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-600 dark:text-slate-300">Name</th>
                          <th className="px-3 py-2 text-left font-medium text-slate-600 dark:text-slate-300">Email</th>
                          <th className="px-3 py-2 text-center font-medium text-slate-600 dark:text-slate-300">Photos</th>
                          <th className="px-3 py-2 text-center font-medium text-slate-600 dark:text-slate-300">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                        {csvPreview.previews.slice(0, 10).map((preview) => (
                          <tr
                            key={preview.row}
                            className={`${
                              !preview.valid
                                ? 'bg-red-50 dark:bg-red-900/10'
                                : preview.match_count === 0
                                ? 'bg-yellow-50 dark:bg-yellow-900/10'
                                : ''
                            }`}
                          >
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-2">
                                {preview.first_photo_thumbnail && (
                                  <img
                                    src={preview.first_photo_thumbnail}
                                    alt=""
                                    className="w-8 h-8 rounded object-cover"
                                  />
                                )}
                                <span className="font-mono text-xs">{preview.reference_number || '-'}</span>
                              </div>
                            </td>
                            <td className="px-3 py-2">{preview.name || '-'}</td>
                            <td className="px-3 py-2 text-slate-500">{preview.email || '-'}</td>
                            <td className="px-3 py-2 text-center">
                              {preview.match_count > 0 ? (
                                <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                                  {preview.match_count}
                                </Badge>
                              ) : (
                                <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
                                  0
                                </Badge>
                              )}
                            </td>
                            <td className="px-3 py-2 text-center">
                              {preview.valid ? (
                                <Check className="w-4 h-4 text-green-500 mx-auto" />
                              ) : (
                                <AlertCircle
                                  className="w-4 h-4 text-red-500 mx-auto"
                                  title={preview.errors?.join(', ')}
                                />
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {csvPreview.previews.length > 10 && (
                    <div className="px-3 py-2 text-center text-xs text-slate-500 bg-slate-50 dark:bg-slate-800 border-t">
                      Showing first 10 of {csvPreview.previews.length} rows
                    </div>
                  )}
                </div>

                {/* Warnings */}
                {csvPreview.invalid_rows > 0 && (
                  <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-red-700 dark:text-red-300">
                        <strong>{csvPreview.invalid_rows} row(s)</strong> have errors and will be skipped.
                        {csvPreview.previews.filter(p => !p.valid).slice(0, 3).map(p => (
                          <div key={p.row} className="text-xs mt-1">
                            Row {p.row}: {p.errors?.join(', ')}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {csvPreview.rows_without_matches > 0 && csvPreview.invalid_rows === 0 && (
                  <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-yellow-700 dark:text-yellow-300">
                        <strong>{csvPreview.rows_without_matches} row(s)</strong> have no matching photos.
                        They will be created but will need photos added manually.
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCloseCsvDialog}
              disabled={csvImporting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCsvImport}
              disabled={!csvPreview || csvPreview.valid_rows === 0 || csvImporting}
              className="bg-[#BF212F] hover:bg-[#8B1923]"
            >
              {csvImporting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Import {csvPreview?.valid_rows || 0} Deliveries
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HeadshotDeliveriesPanel;
